import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Verse } from '../../types/content';
import VerseCard from './VerseCard';
import { contentService } from '../../services/content';

const VersesList: React.FC<{
  userOnly?: boolean;
  beatId?: string;
  /** Owner of the beat being viewed — they alone review every submission. */
  beatProducerId?: string;
}> = ({
  userOnly = false,
  beatId,
  beatProducerId,
}) => {
  const { authState } = useAuth();
  const [verses, setVerses] = useState<Verse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Remove audioRef, currentlyPlaying, and togglePlay logic related to playback

  // Modal state for delete confirmation
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingVerseId, setPendingVerseId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchVerses = useCallback(async () => {
    try {
      const filters: any = {};
      if (userOnly && authState.user?.id) {
        filters.userOnly = true;
        filters.userId = authState.user.id;
      }
      if (beatId) {
        filters.beatId = beatId;
      }
      // The producer reviews every take on their own beat. Everyone else —
      // including other rappers on the same beat — gets published verses plus
      // their own pending one.
      const reviewsThisBeat =
        !!beatProducerId && !!authState.user?.id && beatProducerId === authState.user.id;
      filters.verseStatus = reviewsThisBeat ? 'all' : 'visible';
      const verses = await contentService.getVerses(filters);
      setVerses(verses);
    } catch (err) {
      setError('Error fetching verses');
      console.error('Error fetching verses:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userOnly, beatId, beatProducerId, authState.user?.id]);

  useEffect(() => {
    fetchVerses();
  }, [fetchVerses]);

  useEffect(() => {
    return () => {
      // Remove audioRef.current.pause() and audioRef.current.src = ''
    };
  }, []);

  const requestDelete = (verseId: string) => {
    setPendingVerseId(verseId);
    setConfirmOpen(true);
  };

  const performDelete = async () => {
    if (!pendingVerseId) return;
    setIsDeleting(true);
    try {
      const verse = verses.find(v => v.id === pendingVerseId);
      if (!verse) return;

      await contentService.deleteVerse(verse);

      setVerses(verses.filter(v => v.id !== pendingVerseId));
      setConfirmOpen(false);
      setPendingVerseId(null);
    } catch (err) {
      setError('Error deleting verse');
      console.error('Error deleting verse:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Remove togglePlay function

  if (isLoading) {
    return <div className="text-center py-4">Loading verses...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center py-4">{error}</div>;
  }

  if (verses.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        {userOnly 
          ? 'You haven\'t recorded any verses yet.' 
          : beatId 
            ? 'There are no recorded verses for the selected beat.'
            : 'No verses available.'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {verses.map(verse => (
        <VerseCard key={verse.id} verse={verse} onDelete={requestDelete} onVote={fetchVerses} />
      ))}

      {/* Delete Confirmation Modal */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-3xl shadow-large border border-gray-200 p-6 max-w-sm w-full">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              <span className="text-primary">Delete Verse?</span>
            </h2>
            <p className="text-gray-600 mb-4">Are you sure you want to delete this verse?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setConfirmOpen(false); setPendingVerseId(null); }}
                className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-900 transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={performDelete}
                className={`px-4 py-2 rounded-xl ${isDeleting ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary-dark'} transition-colors`}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersesList; 